import { Card, Checkbox, Col, DatePicker, Form, Input, InputNumber, Row, Select } from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { FormInstance, Rule } from "antd/lib/form";
import { useTranslation } from "react-i18next";
import ReactQuill from "react-quill-new";
import { useSelector } from "react-redux";
import InputAddon from "../../../../../../common/components/form/addons/InputAddon";
import LabelWithTooltip from "../../../../../../common/components/form/labels/LabelWithTooltip";
import Ellipsis from "../../../../../../common/components/views/Ellipsis";
import { rowGutter } from "../../../../../../common/constants";
import { Permission } from "../../../../../../common/security/authorization/enums";
import { RootState } from "../../../../../../common/types";
import {
  datePickerClearableProps,
  datePickerFormItemProps,
  datePickerStandardProps,
  disableDatePickerOutOfMaxDate,
  disableDatePickerOutOfMaxDateIncluded,
  disableDatePickerOutOfMinDate,
  disableDatePickerOutOfMinDateIncluded,
  inputNumberDecimalStandardProps,
  quillEditorStandardProps,
  quillFormItemProps,
  selectStandardProps
} from "../../../../../../common/utils/formUtils";
import { regexPatterns, validations } from "../../../../../../common/utils/validationUtils";
import { selectHasPermissions } from "../../../../../auth/ducks";
import { InvestmentFeeType, InvestmentRiskLevel, InvestmentType } from "../../../../enums";
import { CreateUpdateContractGainerRecord, CreateUpdateInvestmentContract } from "../../../../types";
import { calculateContractStatus } from "../../../../utils";
import ContractStatusTag from "../../../ContractStatusTag";

interface Props {
  form: FormInstance<CreateUpdateInvestmentContract>;
  isVerifiedContract: boolean;
  privilegedChangesPermission: Permission;
}

const colSpan = 4;

export const InvestmentContractFormDataSection = ({ form, isVerifiedContract, privilegedChangesPermission }: Props) => {
  const { t } = useTranslation();

  const signDate = Form.useWatch("signDate", form);
  const status = Form.useWatch("status", form);
  const effectiveEndDate = Form.useWatch("effectiveEndDate", form);
  const cancellationDate = Form.useWatch("cancellationDate", form);
  const transferredToOtherBroker = Form.useWatch("transferredToOtherBroker", form);

  const hasPrivilegedChangesPermission = useSelector<RootState, boolean>(state =>
    selectHasPermissions(privilegedChangesPermission)(state)
  );

  const disablePrivilegedInputs = isVerifiedContract && !hasPrivilegedChangesPermission;

  const handleSignDateChange = () => {
    handleStatusDefiningDateChange();
    const signDate = form.getFieldValue(["signDate"]);
    const records = [...form.getFieldValue(["gainerRecords"])] as CreateUpdateContractGainerRecord[];
    records[0] = { ...(records[0] as CreateUpdateContractGainerRecord), startDate: signDate };
    form.setFieldsValue({ gainerRecords: records });
  };

  const handleStatusDefiningDateChange = () => {
    const { signDate, effectiveEndDate, cancellationDate, transferredToOtherBrokerDate } = form.getFieldsValue([
      ["signDate"],
      ["effectiveEndDate"],
      ["cancellationDate"],
      ["transferredToOtherBrokerDate"]
    ]) as CreateUpdateInvestmentContract;
    form.setFieldsValue({
      status: calculateContractStatus({
        startDate: signDate,
        endDate: effectiveEndDate,
        cancellationDate: cancellationDate,
        transferredToOtherBrokerDate: transferredToOtherBrokerDate
      })
    });
  };

  const handleTransferredToOtherBrokerChange = (event: CheckboxChangeEvent) => {
    if (!event.target.checked) {
      form.setFieldsValue({ transferredToOtherBrokerDate: undefined });
      handleStatusDefiningDateChange();
    }
  };

  const getCancellationDateFormItemRules = (): Rule[] => {
    const rules = [];

    if (signDate) {
      rules.push(validations.notBefore(signDate, t("contract.attrs.signDate")));
    }
    if (effectiveEndDate) {
      rules.push(validations.notSameOrAfter(effectiveEndDate, t("contract.attrs.effectiveEndDate")));
    }

    return rules.length > 0 ? rules : [validations.none];
  };

  const getTransferredToOtherBrokerDateFormItemRules = (): Rule[] => {
    const rules = [];

    if (signDate) {
      rules.push(validations.notSameOrBefore(signDate, t("contract.attrs.signDate")));
    }

    if (cancellationDate) {
      rules.push(validations.notAfter(cancellationDate, t("contract.attrs.cancellationDate")));
    } else if (effectiveEndDate) {
      rules.push(validations.notAfter(effectiveEndDate, t("contract.attrs.effectiveEndDate")));
    }

    if (transferredToOtherBroker) {
      rules.push(validations.notNull);
    }

    return rules.length > 0 ? rules : [validations.none];
  };

  return (
    <Card
      type="inner"
      className="card-box card-box--inner-extra"
      title={t("contract.sections.contractData")}
      extra={status ? <ContractStatusTag status={status} /> : undefined}
    >
      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name="mediationReportSignDate"
            label={t("contract.attrs.mediationReportSignDate")}
            rules={
              signDate
                ? [validations.notNull, validations.notAfter(signDate, t("contract.attrs.signDate"))]
                : [validations.notNull]
            }
            {...datePickerFormItemProps}
          >
            <DatePicker
              {...datePickerStandardProps}
              disabledDate={current => (signDate ? disableDatePickerOutOfMaxDate(current, signDate) : false)}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="signDate"
            label={t("contract.attrs.signDate")}
            rules={[validations.notNull]}
            {...datePickerFormItemProps}
          >
            <DatePicker {...datePickerStandardProps} onChange={handleSignDateChange} />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="effectiveEndDate"
            label={t("contract.attrs.effectiveEndDate")}
            rules={[signDate ? validations.notSameOrBefore(signDate, t("contract.attrs.signDate")) : validations.none]}
            {...datePickerFormItemProps}
          >
            <DatePicker
              {...datePickerClearableProps}
              disabledDate={current => (signDate ? disableDatePickerOutOfMinDateIncluded(current, signDate) : false)}
              onChange={handleStatusDefiningDateChange}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="cancellationDate"
            label={t("contract.attrs.cancellationDate")}
            rules={getCancellationDateFormItemRules()}
            {...datePickerFormItemProps}
          >
            <DatePicker
              {...datePickerClearableProps}
              disabledDate={current =>
                (!!signDate && disableDatePickerOutOfMinDate(current, signDate)) ||
                (!!effectiveEndDate && disableDatePickerOutOfMaxDateIncluded(current, effectiveEndDate))
              }
              onChange={handleStatusDefiningDateChange}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="monthlyPayment"
            label={t("contract.attrs.monthlyPayment")}
            rules={[validations.notNull, validations.minNumber(0)]}
          >
            <InputNumber {...inputNumberDecimalStandardProps} addonAfter={<InputAddon type="euro" />} />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="onetimePayment"
            label={t("contract.attrs.onetimePayment")}
            rules={[validations.notNull, validations.minNumber(0)]}
          >
            <InputNumber {...inputNumberDecimalStandardProps} addonAfter={<InputAddon type="euro" />} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name="investmentDuration"
            label={t("contract.attrs.investmentDuration")}
            rules={[validations.minNumber(1), validations.maxNumber(100)]}
          >
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="investmentType"
            label={t("contract.enums.investmentType._label")}
            rules={[validations.notNull]}
          >
            <Select
              {...selectStandardProps}
              options={Object.keys(InvestmentType).map(type => ({
                value: type,
                label: t("contract.enums.investmentType." + type)
              }))}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item name="riskLevel" label={t("contract.enums.investmentRiskLevel._label")} rules={[validations.none]}>
            <Select
              {...selectStandardProps}
              allowClear
              options={Object.keys(InvestmentRiskLevel).map(type => ({
                value: type,
                label: t("contract.enums.investmentRiskLevel." + type)
              }))}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="computedRiskLevel"
            label={t("contract.enums.investmentComputedRiskLevel._label")}
            rules={[validations.none]}
          >
            <Select
              {...selectStandardProps}
              allowClear
              options={Object.keys(InvestmentRiskLevel).map(type => ({
                value: type,
                label: t("contract.enums.investmentComputedRiskLevel." + type)
              }))}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="prepaidTargetAmount"
            label={<Ellipsis>{t("contract.attrs.prepaidTargetAmount")}</Ellipsis>}
            rules={[validations.minNumber(0)]}
          >
            <InputNumber {...inputNumberDecimalStandardProps} addonAfter={<InputAddon type="euro" />} />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="feePaymentsCount"
            label={t("contract.attrs.feePaymentsCount")}
            rules={[validations.notNull, validations.minNumber(0)]}
          >
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name="feeTotalAmount"
            label={t("contract.attrs.feeTotalAmount")}
            rules={[validations.notNull, validations.minNumber(0)]}
          >
            <InputNumber {...inputNumberDecimalStandardProps} addonAfter={<InputAddon type="euro" />} />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item name="feeType" label={t("contract.enums.investmentFeeType._label")} rules={[validations.notNull]}>
            <Select
              {...selectStandardProps}
              options={Object.values(InvestmentFeeType).map(type => ({
                value: type,
                label: t("contract.enums.investmentFeeType." + type)
              }))}
              disabled={disablePrivilegedInputs}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="managementFee"
            label={t("contract.attrs.managementFee")}
            rules={[validations.notNull, validations.minNumber(0), validations.maxNumber(100)]}
          >
            <InputNumber {...inputNumberDecimalStandardProps} addonAfter={<InputAddon type="percentage" />} />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="transferredFromOtherBroker"
            className="form-item-without-label"
            valuePropName="checked"
            rules={[validations.none]}
            initialValue={false}
          >
            <Checkbox style={{ maxHeight: 22 }}>
              <LabelWithTooltip
                label={t("contract.attrs.transferredFromOtherBroker")}
                tooltip={t("contract.helpers.transferredFromOtherBrokerDesc")}
              />
            </Checkbox>
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="transferredToOtherBroker"
            className="form-item-without-label"
            valuePropName="checked"
            rules={[validations.none]}
            initialValue={false}
          >
            <Checkbox style={{ maxHeight: 22 }} onChange={handleTransferredToOtherBrokerChange}>
              <LabelWithTooltip
                label={t("contract.attrs.transferredToOtherBroker")}
                tooltip={t("contract.helpers.transferredToOtherBrokerDesc")}
              />
            </Checkbox>
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="transferredToOtherBrokerDate"
            label={
              <LabelWithTooltip
                label={t("contract.attrs.transferredToOtherBrokerDate")}
                tooltip={t("contract.helpers.transferredToOtherBrokerDateDesc")}
              />
            }
            rules={getTransferredToOtherBrokerDateFormItemRules()}
            {...datePickerFormItemProps}
          >
            <DatePicker
              {...datePickerClearableProps}
              disabled={!transferredToOtherBroker}
              disabledDate={current =>
                (!!signDate && disableDatePickerOutOfMinDateIncluded(current, signDate)) ||
                (!!cancellationDate && disableDatePickerOutOfMaxDate(current, cancellationDate)) ||
                (!!effectiveEndDate && disableDatePickerOutOfMaxDate(current, effectiveEndDate))
              }
              onChange={handleStatusDefiningDateChange}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item name="pid" label={t("contract.attrs.pid")} rules={[validations.size(1, 64)]}>
            <Input />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item name="offerLabel" label={t("contract.attrs.offerLabel")} rules={[validations.size(1, 255)]}>
            <Input />
          </Form.Item>
        </Col>

        <Col span={colSpan * 2}>
          <Form.Item
            name="productInfoLink"
            label={t("contract.attrs.productInfoLink")}
            rules={[validations.size(1, 255), validations.pattern(regexPatterns.webPageRegex)]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter} className="margin-top-small">
        <Col span={24}>
          <Form.Item
            name="note"
            label={t("contract.attrs.note")}
            rules={[validations.size(1, 8192)]}
            {...quillFormItemProps}
          >
            <ReactQuill {...quillEditorStandardProps} />
          </Form.Item>
        </Col>
      </Row>
    </Card>
  );
};
